/** Pure Boost Climb helpers. Prices come from server climb_targets. */

export const PREVIEW_LIMIT = 5;
export const FLIP_MS = 620;
export const DELTA_MS = 1700;

export const IDLE_BOOST = { status: "idle" };

export function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function hasClimbTargets(entity) {
  return Array.isArray(entity?.climb_targets) && entity.climb_targets.length > 0;
}

export function targetForRank(entity, targetRank) {
  return (entity?.climb_targets || []).find((target) => target.target_rank === targetRank) || null;
}

export function visibleClimbTargets(targets, expanded) {
  const list = Array.isArray(targets) ? targets : [];
  if (expanded) return list;
  return list.slice(0, PREVIEW_LIMIT);
}

export function overtakenLabels(target) {
  return (target?.overtaken || [])
    .map((row) => row?.label || row?.name)
    .filter(Boolean);
}

export function passPhrase(target) {
  const labels = overtakenLabels(target);
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

export function jumpLabel(positions) {
  const count = Number(positions) || 0;
  return count === 1 ? "Jump 1 position" : `Jump ${count} positions`;
}

export function quoteComparison(quotedAmount, freshAmount) {
  if (quotedAmount == null || freshAmount == null) return "missing";
  const quoted = Number(quotedAmount);
  const fresh = Number(freshAmount);
  if (Number.isNaN(quoted) || Number.isNaN(fresh)) return "missing";
  if (fresh === quoted) return "unchanged";
  if (fresh > quoted) return "increased";
  return "decreased";
}

/** Unchanged quotes can be charged. Any change must be confirmed again. */
export function paymentAmountForQuote(quotedAmount, freshAmount) {
  const comparison = quoteComparison(quotedAmount, freshAmount);
  if (comparison === "unchanged") {
    return { proceed: true, changed: false, amount: Number(quotedAmount), comparison };
  }
  if (comparison === "increased" || comparison === "decreased") {
    return { proceed: false, changed: true, amount: Number(freshAmount), comparison };
  }
  return { proceed: false, changed: false, amount: null, comparison };
}

export function landedRank(result) {
  if (result == null) return null;
  if (result.new_rank != null) return result.new_rank;
  if (result.newRank != null) return result.newRank;
  return null;
}

/**
 * Animation-only deltas from the board as it looked before the reorder.
 * Positive means the backed token climbed. Passed rows drop by one.
 * Uses the rank the server actually returned.
 */
export function climbDeltas(entities, coinId, oldRank, newRank) {
  const deltas = {};
  const from = Number(oldRank);
  const to = Number(newRank);
  if (!from || !to || to >= from) return deltas;
  if ((entities || []).some((entity) => entity.id === coinId)) {
    deltas[coinId] = from - to;
  }
  (entities || []).forEach((entity) => {
    if (entity.id === coinId) return;
    const rank = entity.leaderboard_rank || entity.rank;
    if (rank >= to && rank < from) deltas[entity.id] = -1;
  });
  return deltas;
}

/** Move one entity to the server rank. previous_rank is left untouched. */
export function reorderForClimb(entities, coinId, newRank) {
  const list = (entities || []).map((entity) => ({ ...entity }));
  const from = list.findIndex((entity) => entity.id === coinId);
  const rank = Number(newRank);
  if (from < 0 || !rank || rank < 1) return entities || [];
  const [item] = list.splice(from, 1);
  list.splice(Math.min(rank - 1, list.length), 0, item);
  return list.map((entity, index) => ({
    ...entity,
    leaderboard_rank: index + 1,
  }));
}

export function boostReducer(state, action) {
  switch (action.type) {
    case "preview":
      if (state.status === "confirming" || state.status === "processing" || state.status === "success") {
        return state;
      }
      if (!action.coinId) return state;
      return { status: "previewing", coinId: action.coinId };
    case "clear_preview":
      if (state.status !== "previewing") return state;
      if (action.coinId && state.coinId !== action.coinId) return state;
      return IDLE_BOOST;
    case "select_target":
      if (state.status === "processing" || state.status === "success") return state;
      if (!action.coinId || action.targetRank == null || action.amount == null) return state;
      return {
        status: "confirming",
        coinId: action.coinId,
        targetRank: action.targetRank,
        quotedAmount: action.amount,
        currentRank: action.currentRank,
        positionsGained: action.positionsGained,
        overtaken: action.overtaken || [],
        priceChanged: false,
        freshAmount: null,
        revalidating: false,
        error: null,
      };
    case "revalidate_start":
      if (state.status !== "confirming") return state;
      return { ...state, revalidating: true, error: null };
    case "quote_unchanged":
      if (state.status !== "confirming") return state;
      return {
        ...state,
        revalidating: false,
        priceChanged: false,
        freshAmount: state.quotedAmount,
        error: null,
      };
    case "quote_changed":
      if (state.status !== "confirming") return state;
      return {
        ...state,
        revalidating: false,
        priceChanged: true,
        freshAmount: action.amount,
        error: null,
      };
    case "revalidate_error":
      if (state.status !== "confirming") return state;
      return {
        ...state,
        revalidating: false,
        error: action.message || "Could not refresh the rank price.",
      };
    case "start_payment":
      if (state.status !== "confirming") return state;
      return {
        status: "processing",
        coinId: state.coinId,
        targetRank: state.targetRank,
        requestedRank: action.requestedRank ?? state.targetRank,
        amount: action.amount,
        currentRank: state.currentRank,
      };
    case "payment_success":
      return {
        status: "success",
        phase: "flip",
        coinId: action.coinId,
        oldRank: action.oldRank,
        newRank: action.newRank,
        requestedRank: action.requestedRank,
        amount: action.amount,
      };
    case "climb":
      if (state.status !== "success") return state;
      return { ...state, phase: "climb" };
    case "cancel":
    case "reset":
      return IDLE_BOOST;
    default:
      return state;
  }
}
